<template>
  <div class="ladder">
    <ftu-block
      dismiss-key="ladder"
      icon="cl_ladder"
      title="This is the Ladder"
      description="The Ladder allows you to browse all the Card Ladder verified cards and sales. You can search, sort, and filter these cards to find specific cards and watch how different cards trend up and down for each of the algorithms. Click on a card to view its profile and start digging into the data."
    />

    <page-header title="Ladder">
      <template #actions>
        <saved-filters
          type="ladder"
          :query="query"
          :filters="filters"
          :is-filtering="isFiltering"
          @select="applySavedFilter"
        />
      </template>
    </page-header>

    <align flex collapse-end direction="flex-start">
      <shared-filters
        show-recent-searches
        placeholder="Search"
        :filters.sync="filters"
        :query.sync="query"
        :properties="defaultFilters"
        @clear="clearFilters"
      />

      <align small-gap>
        <pill-dropdown
          v-if="isStaff"
          v-model="employee"
          :options="employeeFilterOptions"
        />

        <direction-sort-options
          :sort.sync="sort"
          :direction.sync="direction"
          :options="sortOptions"
        />
      </align>
    </align>

    <filter-results
      :is-filtering="isFiltering"
      :filters="filters"
      :results="numResults && numResults < 10000 ? numResults : null"
      @clear="clearFilters"
    />

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="search_off"
        title="There are no results for your query"
        description="Try searching for something else"
      />

      <template>
        <div class="list">
          <card-list-item
            v-for="card in cards"
            :key="card.id"
            :to="{ path: '/ladder', query: { ...$route.query, cardId: card.id } }"
            :card="card"
            :fields="itemFields"
            as="router-link"
          />
        </div>

        <infinite-scroll
          :loading="loading"
          :has-more-data="hasMoreData"
          @load="nextPage"
        />
      </template>
    </div>

    <scroll-top />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import clone from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import * as Sentry from '@sentry/vue';
import debounce from 'lodash.debounce';

import { DEFAULT_LADDER_FILTERS, LADDER_SORT_OPTIONS } from '@cardladder/shared/constants';

import CardListItem from '@cardladder/shared/components/CardListItem';
import DirectionSortOptions from '@cardladder/shared/components/DirectionSortOptions';

import FilterResults from '@/components/FilterResults';
import SharedFilters from '@/components/SharedFilters';

import { searchService } from '@/services';

export default {
  components: {
    CardListItem,
    DirectionSortOptions,
    FilterResults,
    SharedFilters,
  },

  data() {
    return {
      limit: 20,
      loading: false,
      loaded: false,
      cards: [],
      page: 1,
      hasMoreData: true,
      numResults: 0,
      sortOptions: LADDER_SORT_OPTIONS,
      defaultFilters: DEFAULT_LADDER_FILTERS,
    };
  },

  created() {
    this.reset = debounce(this.reset, 500);
    this.reset();
  },

  watch: {
    sort() {
      this.reset();
    },

    direction() {
      this.reset();
    },

    query() {
      this.reset();
    },

    filters: {
      handler() {
        this.reset();
      },
      deep: true,
    },

    page(value) {
      if (value > 1) {
        this.search();
      }
    },

    '$route.query.q': {
      handler(value) {
        if (value) {
          this.query = value;

          const query = { ...this.$route.query };
          delete query.q;
          this.$router.replace({ query });
        }
      },
      immediate: true,
    },

    '$route.query.s': {
      handler(value) {
        if (value) {
          this.sort = value;

          const query = { ...this.$route.query };
          delete query.s;
          this.$router.replace({ query });
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      employeeOptions: (state) => state.employeeOptions,
    }),

    ...mapGetters({
      isStaff: 'isStaff',
    }),

    employeeFilterOptions() {
      return [
        { value: 'all', label: 'All' },
        ...this.employeeOptions,
      ];
    },

    sort: {
      get() {
        const activeSort = this.sortOptions.find((value) => value === this.$store.state.ladderSort);

        if (activeSort) {
          return this.$store.state.ladderSort;
        }

        return this.sortOptions[0];
      },

      set(value) {
        this.$store.commit('SET_LADDER_SORT', value);
      },
    },

    direction: {
      get() {
        return this.$store.state.ladderDirection;
      },

      set(value) {
        this.$store.commit('SET_LADDER_DIRECTION', value);
      },
    },

    filters: {
      get() {
        return this.$store.state.ladderFilterMap;
      },

      set(value) {
        this.$store.commit('SET_LADDER_FILTERS', value);
      },
    },

    query: {
      get() {
        return this.$store.state.ladderQuery;
      },

      set(value) {
        this.$store.commit('SET_LADDER_QUERY', value);
      },
    },

    employee: {
      get() {
        const activeValue = this.employeeFilterOptions.find(({ value }) => value === this.$store.state.ladderFilterMap.employee);

        if (activeValue) {
          return this.$store.state.ladderFilterMap.employee;
        }

        return this.employeeFilterOptions[0].value;
      },

      set(value) {
        this.filters = {
          ...this.filters,
          employee: value,
        };
      },
    },

    showEmptyState() {
      return !this.loading && this.loaded && this.cards.length === 0;
    },

    isFiltering() {
      return Boolean(this.query?.trim().length > 0) || !isEqual(this.filters, DEFAULT_LADDER_FILTERS);
    },

    itemFields() {
      const fields = [...LADDER_SORT_OPTIONS]
        .sort((a, b) => {
          if (a === this.sort) return -1;
          if (b === this.sort) return 1;

          return 0;
        })
        .slice(0, 3)
        .reverse();

      if (fields[1] === 'currentValue') {
        fields[1] = fields[0];
        fields[0] = 'currentValue';
      }

      return fields;
    },
  },

  methods: {
    reset() {
      this.page = 1;
      this.hasMoreData = true;
      this.loaded = false;

      this.search(true);
    },

    async search(reset = false) {
      try {
        this.loading = true;

        const data = await searchService.search({
          index: 'cards',
          query: this.query,
          page: this.page,
          limit: this.limit,
          filters: this.filters,
          sort: this.sort,
          direction: this.direction,
        });

        const cards = data.hits;

        this.numResults = data.totalHits;

        if (reset) {
          this.cards = cards;
        } else {
          this.cards = [...this.cards, ...cards];
        }

        if (cards.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        Sentry.captureException(e);
      }

      this.loaded = true;
      this.loading = false;
    },

    nextPage() {
      this.page += 1;
    },

    clearFilters() {
      this.filters = {
        ...clone(this.defaultFilters),
        employee: this.filters.employee,
      };
    },

    applySavedFilter(savedFilter) {
      this.query = savedFilter.query;
      this.filters = {
        ...clone(this.defaultFilters),
        ...savedFilter.filters,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.ladder {
  @include container;
  min-height: 100%;
}

.results {
  position: relative;
  flex: 1;
  margin-top: $gp;
}

.list {
  @media($medium-down) {
    margin: 0 (-$gp);
  }
}
</style>
